import React, { useCallback } from 'react';
import clsx from 'clsx';
import { IntlShape, useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { ReleasePost } from '../../../../../redux/api/fymAPI.types';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { planPostState } from './planModel';
import { planMessages } from './planMessages';
import {
  POST_STATE_CLASS,
  POST_STATE_MESSAGES,
  postPlatformName,
  postStepLine,
  releasePostName,
} from './planPostLabels';
import { planPostTime } from './rolloutDayCopy';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Plan tab; each part uses its own slice of it */
import styles from './Plan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReleasePostRowProps {
  post: ReleasePost;
  /** "Try again" of a failed post: the Calendar on that post. */
  onOpenInCalendar: (postUuid: string) => void;
}

/** "Countdown · Story r1", "AI reel 30s r2", "AI reel, day 5", "Post". */
function postLine(intl: IntlShape, post: ReleasePost): string {
  const name = releasePostName(intl, post);
  const revision = post.kitAsset?.version ?? post.reel?.revision ?? null;
  const asset = revision === null ? name : intl.formatMessage(planMessages.revision, { name, revision });
  return postStepLine(intl, post, asset);
}

/**
 * One row of "Posts of this release" (design f1): when, what it uses (the asset revision rN or the
 * reel), the channel, the state, and the action: View post, Try again, or Edit in Composer.
 */
const ReleasePostRowComponent: React.FC<ReleasePostRowProps> = ({ post, onOpenInCalendar }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const state = planPostState(post.status);
  const edit = useCallback(() => router.push(SMLRoutes.promoteComposer, { postUuid: post.uuid }), [post.uuid, router]);
  const tryAgain = useCallback(() => onOpenInCalendar(post.uuid), [onOpenInCalendar, post.uuid]);
  const note = post.failReason ?? (post.linkedBy === 'smartLink' ? intl.formatMessage(planMessages.bySmartLink) : null);

  let action: React.ReactNode;
  if (state === 'posted') {
    action =
      post.externalPostUrl === null ? null : (
        <a className={styles.rowLink} href={post.externalPostUrl} target="_blank" rel="noopener noreferrer">
          {intl.formatMessage(planMessages.viewPost)}
        </a>
      );
  } else if (state === 'failed') {
    action = (
      <button type="button" className={styles.rowLink} onClick={tryAgain}>
        {intl.formatMessage(planMessages.tryAgain)}
      </button>
    );
  } else {
    action = (
      <button type="button" className={styles.rowLink} onClick={edit}>
        {intl.formatMessage(planMessages.editInComposer)}
      </button>
    );
  }

  return (
    <tr data-testid="release-post-row">
      <td className={styles.cellWhen}>{planPostTime(intl, post.scheduledAt)}</td>
      <td>
        <b className={styles.cellPost}>{postLine(intl, post)}</b>
        {note !== null && <small className={styles.cellNote}>{note}</small>}
      </td>
      <td>{postPlatformName(post.platform)}</td>
      <td>
        <span className={clsx(styles.stateChip, POST_STATE_CLASS[state])}>
          {intl.formatMessage(POST_STATE_MESSAGES[state])}
        </span>
      </td>
      <td className={styles.cellAction}>{action}</td>
    </tr>
  );
};

export const ReleasePostRow = TypedMemo(ReleasePostRowComponent);
